<template>
  <div>
    <modal data-tour="webhook_modal" class="fm-webhook-modal">
      <template slot="header">
        <h4 class="modal-title">
          {{ creatingNewHook ? "Add" : "Edit" }} Webhook
        </h4>
        <md-button
          class="md-simple md-just-icon md-round modal-default-button"
          @click="closeModal"
        >
          <md-icon>clear</md-icon>
        </md-button>
      </template>

      <template slot="body">
        <div class="md-card HookTextFormGroup">
          <md-field :class="{ 'md-invalid': $v.localWebhook.name.$error }">
            <label>Name</label>
            <md-input
              v-model.trim="localWebhook.name"
              placeholder="Name"
              class="full-width"
              @input="validateForm"
            >
            </md-input>
            <span class="md-error" v-if="!$v.localWebhook.name.required"
              >The hook name is required</span
            >
            <span class="md-error" v-else-if="!$v.localWebhook.name.maxLength"
              >Hook name is too long</span
            >
            <span class="md-error" v-else-if="!$v.localWebhook.name.minlength"
              >Hook name is too short</span
            >
            <help-link
              :link="helpLink.base.link"
              tooltipText="Enter the name of the hook."
            ></help-link>
          </md-field>
          <md-field
            :class="{ 'md-invalid': $v.webhookUrlForValidation.$error }"
          >
            <select v-model="urlProtocol">
              <option>https://</option>
              <option>http://</option>
            </select>
            <label>Url</label>
            <md-input
              v-model.trim="localWebhook.url"
              placeholder="Url"
              class="full-width"
              @input="validateForm"
            ></md-input>
            <span class="md-error" v-if="!$v.webhookUrlForValidation.required"
              >The URL is required</span
            >
            <span class="md-error" v-else-if="!$v.webhookUrlForValidation.url"
              >The URL is invalid</span
            >
            <help-link
              :link="helpLink.base.link"
              tooltipText="Enter the URL of the API the hook will call."
            ></help-link>
          </md-field>
        </div>

        <div class="md-layout">
          <div class="md-layout-item md-size-100 hookEventSwitchGroupWrapper">
            <div class="hookEventSwitchGroupLabel">Event Category</div>
            <div class="md-card HookEventCategoryGroup">
              <div class="radio-buttons hookEventCategoryRadioButtons">
                <md-radio
                  v-if="context == 'app'"
                  v-model="hookEventType"
                  value="client"
                  >Client</md-radio
                >
                <md-radio v-model="hookEventType" value="channel"
                  >Channel</md-radio
                >
                <md-radio v-model="hookEventType" value="connection"
                  >Connection</md-radio
                >
              </div>
            </div>
            <div class="hookEventSwitchGroupLabel">Event Lifecycle</div>
            <div class="minimumEventsErrorMessageContainer">
              <div class="md-card HookEventsSwitchGroup">
                <div class="md-card-content">
                  <div class="md-layout">
                    <template v-if="hookEventType == 'client' && !isSubscriptionLoading">
                      <div class="md-layout-item md-size-50">
                        <div class="hookEventSwitchGroupLabel">Lifecycle</div>
                        <div class="fm-tooltip-group">
                          <md-switch
                            v-model="localEvents['client.message'].value"
                            class="HookEventSwitch"
                            >Client Message</md-switch
                          >
                          <help-link
                            link="https://help.frozenmountain.com/docs/liveswitch-cloud/webhooks#ClientMessage"
                            tooltipText="Triggers when a client sends a message."
                          ></help-link>
                        </div>
                        <div class="fm-tooltip-group">
                          <md-switch
                            v-model="localEvents['client.registered'].value"
                            class="HookEventSwitch"
                            >Client Registered</md-switch
                          >
                          <help-link
                            :link="helpLink.application.clientRegistered.link"
                            tooltipText="Triggers when a client has registered to the server."
                          ></help-link>
                        </div>
                        <div class="fm-tooltip-group">
                          <md-switch
                            v-model="localEvents['client.unregistered'].value"
                            class="HookEventSwitch"
                            >Client Unregistered</md-switch
                          >
                          <help-link
                            :link="helpLink.application.clientUnregistered.link"
                            tooltipText="Triggers when a client has unregistered from the server."
                          ></help-link>
                        </div>
                        <div class="fm-tooltip-group">
                          <md-switch
                           v-model="localEvents['server.message'].value"
                            class="HookEventSwitch"
                            >Server Message</md-switch
                          >
                          <help-link
                            :link="helpLink.application.serverMessage.link"
                            tooltipText="Triggers when server sends message to entire application, specific user, device, or client in the application."
                          ></help-link>
                        </div>
                      </div>
                      <div class="md-layout-item md-size-50">
                        <div class="hookEventSwitchGroupLabel">Recurring</div>
                        <div class="fm-tooltip-group">
                          <md-switch
                            v-model="localEvents['client.updated'].value"
                            class="HookEventSwitch"
                            >Client Updated</md-switch
                          >
                          <help-link
                            :link="helpLink.application.clientUpdated.link"
                            tooltipText="Triggers when a client's details have been updated."
                          ></help-link>
                        </div>
                      </div>
                    </template>
                    <template v-else-if="hookEventType == 'channel' && !isSubscriptionLoading">
                      <div class="md-layout-item">
                        <div class="hookEventSwitchGroupLabel">Lifecycle</div>
                        <div class="fm-tooltip-group">
                          <md-switch
                            v-model="localEvents['channel.client.joined'].value"
                            class="HookEventSwitch"
                            >Client Joined</md-switch
                          >
                          <help-link
                            :link="helpLink.channel.clientJoined.link"
                            tooltipText="Triggers when a client joins a channel."
                          ></help-link>
                        </div>
                        <div class="fm-tooltip-group">
                          <md-switch
                            v-model="localEvents['channel.client.left'].value"
                            class="HookEventSwitch"
                            >Client Left</md-switch
                          >
                          <help-link
                            :link="helpLink.channel.clientLeft.link"
                            tooltipText="Triggers when a client leaves a channel."
                          ></help-link>
                        </div>
                        <div class="fm-tooltip-group">
                          <md-switch
                          v-model="
                              localEvents['channel.client.message'].value
                            "
                            class="HookEventSwitch"
                            >Client Message</md-switch
                          >
                          <help-link
                            link="https://help.frozenmountain.com/docs/liveswitch-cloud/webhooks#ClientMessage"
                            tooltipText="Triggers when a client sends a message on the channel."
                          ></help-link>
                        </div>
                        <div class="fm-tooltip-group">
                          <md-switch
                            v-model="localEvents['channel.activated'].value"
                            class="HookEventSwitch"
                            >Channel Activated</md-switch
                          >
                          <help-link
                            :link="helpLink.channel.channelActivated.link"
                            tooltipText="Triggers when the first client has joined a channel."
                          ></help-link>
                        </div>
                        <div class="fm-tooltip-group">
                          <md-switch
                            v-model="localEvents['channel.deactivated'].value"
                            class="HookEventSwitch"
                            >Channel Deactivated</md-switch
                          >
                          <help-link
                            :link="helpLink.channel.channelDeactivated.link"
                            tooltipText="Triggers when the last client has left a channel."
                          ></help-link>
                        </div>
                        <div class="fm-tooltip-group">
                          <md-switch
                            v-model="
                              localEvents['channel.server.message'].value
                            "
                            class="HookEventSwitch"
                            >Server Message</md-switch
                          >
                          <help-link
                            :link="helpLink.channel.serverMessage.link"
                            tooltipText="Triggers when the server sends a message to an entire channel, specific user, device, or client in the channel."
                          ></help-link>
                        </div>
                      </div>
                      <div class="md-layout-item">
                        <div class="md-layout-item">
                          <div class="hookEventSwitchGroupLabel">RTMP</div>
                          <div class="fm-tooltip-group">
                            <md-switch
                              v-model="localEvents['channel.rtmp.started'].value"
                              class="HookEventSwitch"
                              :class="features.rtmp ? '' : 'feature-disabled'"
                              :disabled="!features.rtmp"
                              >Started</md-switch
                            ><md-tooltip
                              v-if="!features.rtmp"
                              md-direction="left"
                              >{{ rtmpTooltipFeatureText }}</md-tooltip
                            >
                            <help-link
                              :link="helpLink.channel.rtmpStarted.link"
                              tooltipText="Triggers when an RTMP stream is started."
                            ></help-link>
                          </div>
                          <div class="fm-tooltip-group">
                            <md-switch
                              v-model="localEvents['channel.rtmp.stopped'].value"
                              class="HookEventSwitch"
                              :class="features.rtmp ? '' : 'feature-disabled'"
                              :disabled="!features.rtmp"
                              >Stopped</md-switch
                            ><md-tooltip
                              v-if="!features.rtmp"
                              md-direction="left"
                              >{{ rtmpTooltipFeatureText }}</md-tooltip
                            >
                            <help-link
                              :link="helpLink.channel.rtmpStopped.link"
                              tooltipText="Triggers when an RTMP stream is stopped."
                            ></help-link>
                          </div>
                        </div>
                        <div class="md-layout-item">
                          <div class="hookEventSwitchGroupLabel">Recording</div>
                          <div class="fm-tooltip-group">
                            <md-switch
                              v-model="
                                localEvents['channel.recording.audio.ready'].value
                              "
                              class="HookEventSwitch"
                              :class="features.recording ? '' : 'feature-disabled'"
                              :disabled="!features.recording"
                              >Audio Ready</md-switch
                            ><md-tooltip
                              v-if="!features.recording"
                              md-direction="left"
                              >{{ recordingTooltipFeatureText }}</md-tooltip
                            >
                            <help-link
                              :link="helpLink.channel.channelAudioReady.link"
                              tooltipText="Triggers when an audio-only recording is ready to download."
                            ></help-link>
                          </div>
                          <div class="fm-tooltip-group">
                            <md-switch
                              v-model="
                                localEvents['channel.recording.video.ready'].value
                              "
                              class="HookEventSwitch"
                              :class="features.recording ? '' : 'feature-disabled'"
                              :disabled="!features.recording"
                              >Video Ready</md-switch
                            ><md-tooltip
                              v-if="!features.recording"
                              md-direction="left"
                              >{{ recordingTooltipFeatureText }}</md-tooltip
                            >
                            <help-link
                              :link="helpLink.channel.channelVideoReady.link"
                              tooltipText="Triggers when a video-only recording is ready to download."
                            ></help-link>
                          </div>
                          <div class="fm-tooltip-group">
                            <md-switch
                              v-model="
                                localEvents['channel.recording.ready'].value
                              "
                              class="HookEventSwitch"
                              :class="features.recording ? '' : 'feature-disabled'"
                              :disabled="!features.recording"
                              >Recording Ready</md-switch
                            ><md-tooltip
                              v-if="!features.recording"
                              md-direction="left"
                              >{{ recordingTooltipFeatureText }}</md-tooltip
                            >
                            <help-link
                              :link="helpLink.channel.channelReady.link"
                              tooltipText="Triggers when a recording has finished processing and is ready to download."
                            ></help-link>
                          </div>
                          <div class="fm-tooltip-group">
                            <md-switch
                              v-model="
                                localEvents['channel.recording.expired'].value
                              "
                              class="HookEventSwitch"
                              :class="features.recording ? '' : 'feature-disabled'"
                              :disabled="!features.recording"
                              >Recording Expired</md-switch
                            > <md-tooltip
                              v-if="!features.recording"
                              md-direction="left"
                              >{{ recordingTooltipFeatureText }}</md-tooltip
                            >
                            <help-link
                              :link="helpLink.channel.channelExpired.link"
                              tooltipText="Triggers when a recording's storage time has expired and the recording is no longer available."
                            ></help-link>
                          </div>
                        </div>
                      </div>
                    </template>
                    <template v-else-if="hookEventType == 'connection' && !isSubscriptionLoading">
                      <div class="md-layout-item">
                        <div class="hookEventSwitchGroupLabel">Lifecycle</div>
                        <div class="fm-tooltip-group">
                          <md-switch
                            v-model="
                              localEvents['connection.initializing'].value
                            "
                            class="HookEventSwitch"
                            >Initializing</md-switch
                          >
                          <help-link
                            :link="helpLink.channel.connectionInitializing.link"
                            tooltipText="Triggers when a connection has started signalling."
                          ></help-link>
                        </div>
                        <div class="fm-tooltip-group">
                          <md-switch
                            v-model="localEvents['connection.connecting'].value"
                            class="HookEventSwitch"
                            >Connecting</md-switch
                          >
                          <help-link
                            :link="helpLink.channel.connectionConnecting.link"
                            tooltipText="Triggers when a connection has started connectivity checks."
                          ></help-link>
                        </div>
                        <div class="fm-tooltip-group">
                          <md-switch
                            v-model="localEvents['connection.connected'].value"
                            class="HookEventSwitch"
                            >Connected</md-switch
                          >
                          <help-link
                            :link="helpLink.channel.connectionConnected.link"
                            tooltipText="Triggers when a connection has established connectivity."
                          ></help-link>
                        </div>
                        <div class="fm-tooltip-group">
                          <md-switch
                            v-model="localEvents['connection.failing'].value"
                            class="HookEventSwitch"
                            >Failing</md-switch
                          >
                          <help-link
                            :link="helpLink.channel.connectionFailing.link"
                            tooltipText="Triggers when a connection has encountered an error or has been instructed to retry."
                          ></help-link>
                        </div>
                        <div class="fm-tooltip-group">
                          <md-switch
                            v-model="localEvents['connection.failed'].value"
                            class="HookEventSwitch"
                            >Failed</md-switch
                          >
                          <help-link
                            :link="helpLink.channel.connectionFailed.link"
                            tooltipText="Triggers when a connection has cleaned up resources after failing."
                          ></help-link>
                        </div>
                        <div class="fm-tooltip-group">
                          <md-switch
                            v-model="localEvents['connection.closing'].value"
                            class="HookEventSwitch"
                            >Closing</md-switch
                          >
                          <help-link
                            :link="helpLink.channel.connectionClosing.link"
                            tooltipText="Triggers when a connection has been instructed to close."
                          ></help-link>
                        </div>
                        <div class="fm-tooltip-group">
                          <md-switch
                            v-model="localEvents['connection.closed'].value"
                            class="HookEventSwitch"
                            >Closed</md-switch
                          >
                          <help-link
                            :link="helpLink.channel.connectionClosed.link"
                            tooltipText="Triggers when a connection has cleaned up resources after closing."
                          ></help-link>
                        </div>
                      </div>
                      <div class="md-layout-item">
                        <div class="md-layout-item">
                          <div class="hookEventSwitchGroupLabel">Recurring</div>
                          <div class="fm-tooltip-group">
                            <md-switch
                              v-model="localEvents['connection.stats'].value"
                              class="HookEventSwitch"
                              >Stats</md-switch
                            >
                            <help-link
                              :link="helpLink.channel.connectionStats.link"
                              tooltipText="Triggers when a connection's stats have been captured."
                            ></help-link>
                          </div>
                          <div class="fm-tooltip-group">
                            <md-switch
                              v-model="localEvents['connection.updated'].value"
                              class="HookEventSwitch"
                              >Updated</md-switch
                            >
                            <help-link
                              :link="helpLink.channel.connectionUpdated.link"
                              tooltipText="Triggers when a connection's details have been updated."
                            ></help-link>
                          </div>
                        </div>
                        <div class="md-layout-item">
                          <div
                            class="hookEventSwitchGroupLabel"
                          >
                            Recording
                          </div>
                          <div class="fm-tooltip-group">
                            <md-switch
                              v-model="
                                localEvents['connection.recordings.ready'].value
                              "
                              class="HookEventSwitch"
                              :class="features.recording ? '' : 'feature-disabled'"
                              :disabled="!features.recording"
                              >Connections Ready</md-switch
                            ><md-tooltip
                              v-if="!features.recording"
                              md-direction="left"
                              >{{ recordingTooltipFeatureText }}</md-tooltip
                            >
                            <help-link
                              :link="helpLink.channel.connectionsReady.link"
                              tooltipText="Triggers when all connection recordings have uploaded."
                            ></help-link>
                          </div>
                          <div class="fm-tooltip-group">
                            <md-switch
                              v-model="
                                localEvents['connection.recording.audio.ready']
                                  .value
                              "
                              class="HookEventSwitch"
                              :class="features.recording ? '' : 'feature-disabled'"
                              :disabled="!features.recording"
                              >Audio Ready</md-switch
                            ><md-tooltip
                              v-if="!features.recording"
                              md-direction="left"
                              >{{ recordingTooltipFeatureText }}</md-tooltip
                            >
                            <help-link
                              :link="helpLink.channel.connectionAudioReady.link"
                              tooltipText="Triggers when a connection recording's audio has uploaded."
                            ></help-link>
                          </div>
                          <div class="fm-tooltip-group">
                            <md-switch
                              v-model="
                                localEvents['connection.recording.video.ready']
                                  .value
                              "
                              class="HookEventSwitch"
                              :class="features.recording ? '' : 'feature-disabled'"
                              :disabled="!features.recording"
                              >Video Ready</md-switch
                            ><md-tooltip
                              v-if="!features.recording"
                              md-direction="left"
                              >{{ recordingTooltipFeatureText }}</md-tooltip
                            >
                            <help-link
                              :link="helpLink.channel.connectionVideoReady.link"
                              tooltipText="Triggers when a connection recording's video has uploaded."
                            ></help-link>
                          </div>
                        </div>
                      </div>
                    </template>
                    <div class="md-layout-item md-size-100" v-else-if="isSubscriptionLoading">
                      <loading-spinner/>
                    </div>
                  </div>
                </div>
              </div>
              <div class="minimumEventsErrorMessage">
                <span v-if="featureEventsDisabled">
                  Some events were disabled due to missing recording or RTMP
                  addons. These events must be re-enabled if your
                  subscription regains these addons.<br>
                </span>
                <span v-if="!localEventsAsSubmissionArray.length"
                  >At least one event must be chosen.</span
                >
              </div>
            </div>
            <div class="hookEventSwitchGroupLabel">Advanced</div>
            <div class="md-card HookAdvancedGroup">
              <div class="md-card-content">
                <div class="md-layout">
                  <div class="md-layout-item md-size-50">
                    <div class="fm-tooltip-group">
                      <md-switch
                        v-model="localWebhook.batch"
                        class="HookEventSwitch"
                        >Batch</md-switch
                      >
                      <help-link
                        :link="helpLink.base.link"
                        tooltipText="Send an array of events in each request."
                      ></help-link>
                    </div>
                  </div>
                  <div class="md-layout-item md-size-50">
                    <div class="fm-tooltip-group">
                      <md-switch
                        v-model="localWebhook.disabled"
                        class="HookEventSwitch"
                        >Disabled</md-switch
                      >
                      <help-link
                        :link="helpLink.base.link"
                        tooltipText="Discards all triggered events."
                      ></help-link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template slot="footer">
        <md-button class="md-primary md-raised" @click.stop="saveModal(false)">
          {{ creatingNewHook ? "Create" : "Apply" }}
        </md-button>
        <md-button class="md-danger md-simple" @click.stop="closeModal"
          >Cancel</md-button
        >
      </template>
    </modal>
    <!-- Modal to display notice about events being deleted when category changes -->
    <modal v-if="eventsNoticeModalVisible">
      <template slot="header">
        <h4 class="modal-title">Events Category</h4>
        <md-button
          class="md-simple md-just-icon md-round modal-default-button"
          @click.stop="eventsNoticeModalVisible = false"
        >
          <md-icon>clear</md-icon>
        </md-button>
      </template>
      <template slot="body">
        <div>
          <strong>Note:</strong> Only the events within the current Event
          Category are saved. The selections on your previous Event Category
          have been deselected.<br /><br />
        </div>
        <md-button
          class="md-primary md-raised"
          @click.stop="eventsNoticeModalVisible = false"
          >OK</md-button
        >
      </template>
    </modal>
  </div>
</template>

<script>
import { Modal } from "@/components";
import { WebhookHelpLink, LoadingSpinner } from "@/fmComponents";
import { required, url, minLength, maxLength } from "vuelidate/lib/validators";
import { dataTourActive } from "../../utils/appcues";
import helpLinks from "../../contextualHelpLinks.js";

export default {
  created: function() {
    this.isSubscriptionLoading = true;
    window.Appcues.clearShow();
    // if we are editing a hook
    if (this.localWebhook.id) {
      // repopulate the localEvents
      for (const eventName of this.localWebhook.events) {
        this.localEvents[eventName].value = true;
      }
      // pick the Event Category
      if (this.localWebhook.events.length) {
        let firstEvent = this.localWebhook.events[0];
        let moduleDefaultEventTypeSelected = this.hookEventType;
        let newEventTypeSelected = this.localEvents[firstEvent].type;

        if (moduleDefaultEventTypeSelected != newEventTypeSelected) {
          this.hookEventType = newEventTypeSelected;
          this.loadingLocalEventsForEdit = true; // don't allow watcher to trigger for this change
        }
      }
      // Set the default protocol to the one already being used
      if (this.localWebhook.url) {
        this.urlProtocol = this.localWebhook.url.split("/")[0] + "//";
      }
      // Split the url (http(s) is part of the dropdown). Save if we have a bad URL with multiple protocols.
      if (this.cleanUrlProtocol()) {
        this.saveModal(true);
      }
    }

    this.$store
      .dispatch("subscription/loadSubscription", localStorage.subscriptionId)
      .then((result) => {
        this.isSubscriptionLoading = false;

        // Populate a local enabled feature map
        result.enabledFeatures.map(feature => {
          this.features[feature] = true;
        });

        // Disable any webhook events associated with any disabled features
        for (const [feature, isFeatureEnabled] of Object.entries(this.features)) {
          if (!isFeatureEnabled && this.featureWebhookEvents[feature]) {
            this.featureWebhookEvents[feature].map(eventName => {
              if (this.localEvents[eventName].value) {
                this.localEvents[eventName].value = false;
                this.featureEventsDisabled = true;
              }
            });
          }
        }
      })
      .catch(() => {
        this.isSubscriptionLoading = false;
      });
  },
  components: {
    Modal,
    "help-link": WebhookHelpLink,
    LoadingSpinner
  },
  props: ["localWebhook", "context"],
  data: () => ({
    isSubscriptionLoading: false,
    helpLink: helpLinks.webhooks,
    showAdvancedConfigs: false,
    hookEventType: "channel",
    localEvents: {
      "connection.recording.audio.ready": { value: false, type: "connection", feature: "recording" },
      "connection.recording.video.ready": { value: false, type: "connection", feature: "recording" },
      "connection.recordings.ready": { value: false, type: "connection", feature: "recording" },
      "channel.recording.audio.ready": { value: false, type: "channel", feature: "recording" },
      "channel.recording.video.ready": { value: false, type: "channel", feature: "recording" },
      "channel.recording.ready": { value: false, type: "channel", feature: "recording" },
      "channel.recording.expired": { value: false, type: "channel", feature: "recording" },
      "client.registered": { value: false, type: "client" },
      "client.unregistered": { value: false, type: "client" },
      "client.updated": { value: false, type: "client" },
      "client.message": { value: false, type: "client" },
      "channel.client.left": { value: false, type: "channel" },
      "channel.client.joined": { value: false, type: "channel" },
      "channel.client.message": { value: false, type: "channel" },
      "channel.server.message": { value: false, type: "channel" },
      "channel.activated": { value: false, type: "channel" },
      "channel.deactivated": { value: false, type: "channel" },
      "channel.rtmp.started": { value: false, type: "channel", feature: "rtmp" },
      "channel.rtmp.stopped": { value: false, type: "channel", feature: "rtmp" },
      "connection.initializing": { value: false, type: "connection" },
      "connection.connecting": { value: false, type: "connection" },
      "connection.connected": { value: false, type: "connection" },
      "connection.failing": { value: false, type: "connection" },
      "connection.failed": { value: false, type: "connection" },
      "connection.closing": { value: false, type: "connection" },
      "connection.closed": { value: false, type: "connection" },
      "connection.stats": { value: false, type: "connection" },
      "connection.updated": { value: false, type: "connection" },
      "server.message": { value: false, type: "client" },
    },
    urlProtocol: "https://",
    eventsNoticeModalVisible: false,
    loadingLocalEventsForEdit: false,
    features: {
      rtmp: false,
      recording: false
    },
    featureEventsDisabled: false,
    recordingTooltipFeatureText:
      "Recording must be available in your subscription to enable Recording events.",
    rtmpTooltipFeatureText:
      "RTMP must be available in your subscription to enable RTMP events."
  }),
  watch: {
    hookEventType: {
      deep: true,
      handler(newValue, oldValue) {
        if (!this.loadingLocalEventsForEdit) {
          // don't trigger if initially loading the events & category
          if (this.localEventsAsSubmissionArray.length > 0) {
            // if any events are toggled on
            for (const eventName of this.localEventsAsSubmissionArray) {
              this.localEvents[eventName].value = false; // toggle them off
            }
            this.eventsNoticeModalVisible = true;
          }
        }
        this.loadingLocalEventsForEdit = false; // allow watcher to work again in the future
      }
    }
  },
  computed: {
    localEventsAsSubmissionArray: function() {
      // return localEvents as an array of values for submission to the api, containing the events for those that are toggled on
      return Object.entries(this.localEvents)
        .filter(r => r[1].value == true)
        .map(r => r[0]);
    },
    webhookUrlForValidation: function() {
      return this.urlProtocol + this.localWebhook.url;
    },
    creatingNewHook: function() {
      return !this.localWebhook.id; // if there's an id its not a new hook
    },
    featureWebhookEvents: function() {
      return Object.entries(this.localEvents)
        .reduce((featureWebhookEvents, [eventName,event]) => {
          if (event.feature) {
            if (!featureWebhookEvents[event.feature]) {
              featureWebhookEvents[event.feature] = [];
            }
            featureWebhookEvents[event.feature].push(eventName);
          }
          return featureWebhookEvents;
        }, {});
    }
  },
  methods: {
    saveModal: function(correctingHookUrl) {
      if (!dataTourActive) {
        this.validateForm();
        if (this.$v.$error) return; // can't submit if there's an error
        if (!this.localEventsAsSubmissionArray.length) return; // can't submit if at least one event isn't chosen

        this.localWebhook.url = this.webhookUrlForValidation;
        this.localWebhook.events = this.localEventsAsSubmissionArray;
        this.localWebhook.eventType = this.hookEventType; // this will get removed in parent before sending to api
        delete this.localWebhook.transfer; // delete any pending transfer tracking
        if (!correctingHookUrl) {
          this.$emit("close", this.localWebhook);
        } else {
          this.$emit("autoSave", this.localWebhook, true);
          this.cleanUrlProtocol();
        }
      }
    },
    closeModal: function() {
      if (!dataTourActive) {
        this.localWebhook.url = this.webhookUrlForValidation;
        this.$emit("close", false, false);
      }
    },
    validateForm: function() {
      this.$v.$touch();
    },
    cleanUrlProtocol: function() {
      var count = this.localWebhook.url.match(/https?:\/\//g).length;
      this.localWebhook.url = this.localWebhook.url.replaceAll(
        /https?:\/\//g,
        ""
      );
      return count > 1;
    }
  },
  validations: {
    localWebhook: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(255)
      }
    },
    webhookUrlForValidation: {
      required,
      url
    }
  }
};
</script>

<style>
/* Couldn't get these to work with "deep" inside scoped; extracted them to global */
.fm-webhook-modal .modal-body {
  padding-top: 7px;
}
.modal-mask.fm-webhook-modal .modal-container {
  max-height: 100vh;
  overflow-y: auto;
  max-width: 1000px;
}
@media screen and (max-width: 500px) {
  .modal-mask.fm-webhook-modal .modal-container {
    max-width: 320px;
  }
}
.fm-webhook-modal .md-field {
  margin-bottom: 18px;
}
.fm-webhook-modal .md-field .md-error {
  bottom: -12px;
}
</style>

<style lang="scss" scoped>
@import "../../assets/scss/fm/colours";

.hookEventSwitchGroupWrapper {
  padding: 0px;
  .hookEventCategoryRadioButtons {
    margin-left: 5px;
    display: flex;
    justify-content: space-between;
  }
  .HookEventsSwitchGroup,
  .HookEventCategoryGroup,
  .HookAdvancedGroup {
    margin-top: 0px;
    .md-card-content {
      padding-top: 0px;
      padding-bottom: 5px;
      padding-left: 5px;
      .md-layout-item {
        padding: 0px;
        .fm-tooltip-group {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
        }
      }
    }
  }
  .HookEventsSwitchGroup {
    padding-left: 17px;
  }
  .HookEventCategoryGroup {
    padding-left: 10px;
    padding-right: 10px;
  }
  .HookAdvancedGroup {
    margin-bottom: 0px;
  }
}
.HookTextFormGroup {
  padding-left: 17px;
  padding-right: 17px;
}
.minimumEventsErrorMessageContainer {
  position: relative;
  .minimumEventsErrorMessage {
    color: #ff1744;
    font-size: 12px;
    position: absolute;
    bottom: -35px;
    left: 5px;
    line-height: normal;
    text-align: left;
  }
}
/deep/ .md-radio .md-radio-label {
  color: #333 !important;
}
.text-align-left {
  text-align: left;
}
.md-switch.HookEventSwitch {
  margin-left: 10px;
  margin-top: 10px !important;
  margin-bottom: 10px !important;
  margin-right: 3px;
}

/deep/ .md-switch.HookEventSwitch .md-switch-label {
  padding-left: 5px;
  text-align: left;
  height: auto;
}

.md-switch.HookEventSwitch.md-disabled .md-switch-label {
  color: #aaadb4;
}

.hookEventSwitchGroupLabel {
  margin: 5px;
  font-weight: bold;
  text-align: left;
}

.feature-disabled {
  color: lightgray;
}

.md-tooltip {
  transform: translateX(10px);
  transform: translateY(20px);
}
</style>
